import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

const MENU_WIDTH = 200;
const VIEWPORT_PAD = 8;
const GAP = 4;

/**
 * Placement viewport-safe du menu prospect (portal fixed).
 * Ouvre vers le bas si place suffisante, sinon flip vers le haut.
 * @param {{
 *   triggerRect: { top: number, bottom: number, right: number },
 *   menuHeight: number,
 *   viewportWidth: number,
 *   viewportHeight: number,
 *   menuWidth?: number,
 *   pad?: number,
 *   gap?: number,
 * }} args
 */
export function computeProspectMenuPlacement({
  triggerRect,
  menuHeight,
  viewportWidth,
  viewportHeight,
  menuWidth = MENU_WIDTH,
  pad = VIEWPORT_PAD,
  gap = GAP,
}) {
  const left = Math.min(
    Math.max(pad, triggerRect.right - menuWidth),
    Math.max(pad, viewportWidth - menuWidth - pad),
  );

  const spaceBelow = viewportHeight - triggerRect.bottom - pad;
  const spaceAbove = triggerRect.top - pad;
  const preferBelow = spaceBelow >= menuHeight + gap || spaceBelow >= spaceAbove;

  let placement = preferBelow ? 'below' : 'above';
  let top = preferBelow
    ? triggerRect.bottom + gap
    : triggerRect.top - gap - menuHeight;

  const maxHeight = Math.max(120, viewportHeight - pad * 2);
  let height = menuHeight;
  if (height > maxHeight) {
    height = maxHeight;
    if (placement === 'below') {
      top = Math.min(triggerRect.bottom + gap, viewportHeight - pad - height);
    } else {
      top = Math.max(pad, triggerRect.top - gap - height);
    }
  }

  // Clamp final dans le viewport
  top = Math.min(Math.max(pad, top), viewportHeight - pad - Math.min(height, maxHeight));

  return {
    top,
    left,
    placement,
    maxHeight,
    overflowY: menuHeight > maxHeight ? 'auto' : 'visible',
  };
}

/**
 * Menu actions prospect — portal fixed pour éviter le clipping overflow-x-auto.
 * Placement viewport-safe : flip vers le haut si pas assez de place dessous.
 */
export default function Nr2jProspectRowMenu({
  org,
  open,
  onToggle,
  onClose,
  actions = [],
}) {
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    maxHeight: undefined,
    overflowY: 'visible',
    placement: 'below',
  });

  const reposition = useCallback(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const measured = menuRef.current?.offsetHeight
      || Math.max(160, (actions?.length || 6) * 36 + 8);
    const next = computeProspectMenuPlacement({
      triggerRect: rect,
      menuHeight: measured,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
    setCoords(next);
  }, [open, actions?.length]);

  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    // Second pass après paint pour hauteur réelle du menu
    const id = requestAnimationFrame(() => reposition());
    return () => cancelAnimationFrame(id);
  }, [open, reposition, actions]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    const onScroll = () => onClose?.();
    const onResize = () => reposition();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, onClose, reposition]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
        aria-label="Actions"
        aria-expanded={open}
        data-testid={`nr2j-prospect-menu-btn-${org.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggle?.();
        }}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={menuRef}
            role="menu"
            data-testid={`nr2j-prospect-menu-${org.id}`}
            data-placement={coords.placement}
            className="fixed z-[100] w-[200px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            style={{
              top: coords.top,
              left: coords.left,
              maxHeight: coords.maxHeight,
              overflowY: coords.overflowY,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action) => (
              <button
                key={action.id}
                type="button"
                role="menuitem"
                className={
                  action.danger
                    ? 'block w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50'
                    : 'block w-full px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50'
                }
                onClick={() => {
                  onClose?.();
                  action.onClick?.();
                }}
              >
                {action.label}
              </button>
            ))}
          </div>,
          document.body,
        )
        : null}
    </>
  );
}

export function formatProspectFollowUp(nextFollowUpAt) {
  if (!nextFollowUpAt) return { text: '—', overdue: false };
  try {
    const d = new Date(nextFollowUpAt);
    if (Number.isNaN(d.getTime())) return { text: '—', overdue: false };
    const overdue = d.getTime() < Date.now();
    const label = d.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
    return {
      text: overdue ? `En retard · ${label}` : label,
      overdue,
      label,
    };
  } catch {
    return { text: '—', overdue: false };
  }
}

/**
 * Prochaine action affiché Liste/Kanban (données actuelles uniquement).
 * Priorité : relance échue → prochaine relance → tâches ouvertes → aucune.
 */
export function formatProspectNextAction(org) {
  const fu = formatProspectFollowUp(org?.nextFollowUpAt);
  if (fu.overdue && fu.label) {
    return {
      text: `⚠ Relance en retard · ${fu.label}`,
      overdue: true,
      kind: 'followup',
    };
  }
  if (org?.nextFollowUpAt && fu.label) {
    return {
      text: `Relance : ${fu.label}`,
      overdue: false,
      kind: 'followup',
    };
  }
  const n = Number(org?.openTaskCount) || 0;
  if (n > 0) {
    return {
      text: `${n} tâche${n > 1 ? 's' : ''}`,
      overdue: false,
      kind: 'task',
    };
  }
  return { text: 'Aucune prochaine action', overdue: false, kind: 'none' };
}

/** Colonnes Kanban actives (pas GAGNE / PERDU / CLIENT). */
export const NR2J_KANBAN_STAGES = [
  { id: 'NOUVEAU', label: 'Nouveau', match: ['NOUVEAU'] },
  { id: 'A_CONTACTER', label: 'À contacter', match: ['A_CONTACTER'] },
  { id: 'CONTACTE', label: 'Contacté', match: ['CONTACTE'] },
  { id: 'QUALIFIE', label: 'Qualifié', match: ['QUALIFIE'] },
  { id: 'RDV', label: 'RDV', match: ['RDV'] },
  { id: 'PROPOSITION', label: 'Proposition', match: ['PROPOSITION'] },
  { id: 'NEGOCIATION', label: 'Négociation', match: ['NEGOCIATION'] },
];

export function kanbanColumnForStage(pipelineStage) {
  return NR2J_KANBAN_STAGES.find((col) => col.match.includes(pipelineStage))?.id || null;
}
